import { useState, useEffect } from 'react'
import type { WatchListing, ItemDetail } from '../../types/watch-monitor'
import { getItemDetail } from '../../lib/watch-monitor-api'

function formatPrice(price: number | null, currency: string): string {
  if (price === null) return 'Price not listed'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price)
}

// A listing counts as recently changed if the backend stamped last_changed
// within this window (the scheduled scrape updates it in place on a change).
function changedRecently(iso: string | null, hours = 48): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return false
  return Date.now() - t <= hours * 60 * 60 * 1000
}

// Badge shown on a card whose price/description changed on a recent scrape.
function ChangeBadge({ listing }: { listing: WatchListing }) {
  if (!changedRecently(listing.lastChanged)) return null

  const priceMoved =
    listing.previousPrice !== null &&
    listing.price !== null &&
    listing.previousPrice !== listing.price

  if (priceMoved) {
    const dropped = (listing.price as number) < (listing.previousPrice as number)
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
          dropped ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/20 text-rose-700 dark:text-rose-300'
        }`}
        title={`Price changed from ${formatPrice(listing.previousPrice, listing.currency)}`}
      >
        {dropped ? '▼' : '▲'} {formatPrice(listing.previousPrice, listing.currency)} →{' '}
        {formatPrice(listing.price, listing.currency)}
      </span>
    )
  }

  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-700 dark:text-amber-300">
      Updated
    </span>
  )
}

function SourceBadge({ source }: { source: string }) {
  const colors: Record<string, string> = {
    'Reddit r/watchexchange': 'bg-orange-500/20 text-orange-700 dark:text-orange-300',
    'eBay': 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
    'Chrono24': 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
    'Bezel': 'bg-purple-500/20 text-purple-700 dark:text-purple-300',
    'Watchbox': 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[source] || 'bg-slate-300/50 dark:bg-slate-600/50 text-slate-600 dark:text-slate-300'}`}>
      {source}
    </span>
  )
}

interface Props {
  listings: WatchListing[]
  loading: boolean
  onRunSearch: () => void
  onRemove: (listingId: string) => void
  searchTimestamp: string | null
}

export default function SearchResults({ listings, loading, onRunSearch, onRemove, searchTimestamp }: Props) {
  const [sortBy, setSortBy] = useState<'date' | 'price-asc' | 'price-desc' | 'source'>('date')
  const [filterSource, setFilterSource] = useState<string>('all')
  const [preview, setPreview] = useState<WatchListing | null>(null)

  const sources = ['all', ...Array.from(new Set(listings.map((l) => l.source)))]

  const filtered = listings.filter((l) => filterSource === 'all' || l.source === filterSource)

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return (a.price ?? 0) - (b.price ?? 0)
      case 'price-desc':
        return (b.price ?? 0) - (a.price ?? 0)
      case 'source':
        return a.source.localeCompare(b.source)
      default:
        return (b.dateListed || '').localeCompare(a.dateListed || '')
    }
  })

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onRunSearch}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Searching...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Run Search
              </>
            )}
          </button>
          {searchTimestamp && (
            <span className="text-slate-500 text-xs">
              Last run: {new Date(searchTimestamp).toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
          >
            {sources.map((s) => (
              <option key={s} value={s}>{s === 'all' ? 'All Sources' : s}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded-lg px-3 py-1.5 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none"
          >
            <option value="date">Newest First</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
            <option value="source">Source</option>
          </select>
        </div>
      </div>

      {/* Results count */}
      {listings.length > 0 && (
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          Showing {sorted.length} of {listings.length} listing{listings.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 animate-pulse">
              <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3" />
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2" />
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && listings.length === 0 && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-8 text-center">
          <div className="text-4xl mb-3">🔍</div>
          <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">No results yet</h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Click "Run Search" to scan all marketplaces for your watched items.</p>
        </div>
      )}

      {/* Results list */}
      {!loading && sorted.length > 0 && (
        <div className="space-y-3">
          {sorted.map((listing, i) => (
            <div
              key={listing.listingId ?? listing.url ?? i}
              role="button"
              tabIndex={0}
              onClick={() => setPreview(listing)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setPreview(listing)
                }
              }}
              className="relative w-full text-left flex gap-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-slate-400 dark:hover:border-slate-500 transition-all group cursor-pointer"
            >
              {/* Remove (dismiss) */}
              {listing.listingId && (
                <button
                  type="button"
                  aria-label="Remove listing"
                  title="Remove from results"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove(listing.listingId as string)
                  }}
                  className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center rounded-full text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-200/60 dark:bg-slate-700/60 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors opacity-70 hover:opacity-100 focus:opacity-100"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* Thumbnail */}
              <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-slate-200/50 dark:bg-slate-700/50 flex items-center justify-center">
                {listing.imageUrl ? (
                  <img src={listing.imageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <span className="text-2xl opacity-40">⌚</span>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h3 className="text-slate-900 dark:text-white font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug">
                    {listing.title}
                  </h3>
                  <span className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap pr-7">
                    {formatPrice(listing.price, listing.currency)}
                  </span>
                </div>
                {listing.description && (
                  <p className="text-slate-500 dark:text-slate-400 text-sm mb-3 line-clamp-2">{listing.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <SourceBadge source={listing.source} />
                  <ChangeBadge listing={listing} />
                  {listing.condition && <span>Condition: {listing.condition}</span>}
                  {listing.seller && <span>Seller: {listing.seller}</span>}
                  {listing.dateListed && <span>{new Date(listing.dateListed).toLocaleDateString()}</span>}
                  {listing.matchedWatch && (
                    <span className="text-slate-400 dark:text-slate-600 italic">Matched: {listing.matchedWatch}</span>
                  )}
                  <span className="ml-auto flex items-center gap-1 text-slate-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Preview
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && <PreviewModal listing={preview} onClose={() => setPreview(null)} />}
    </div>
  )
}

// Strip seller-supplied HTML to safe plain text (no new dependency, no XSS risk).
function htmlToText(html: string): string {
  if (!html) return ''
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return (doc.body.textContent || '').replace(/\s+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
  } catch {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }
}

function PreviewModal({ listing, onClose }: { listing: WatchListing; onClose: () => void }) {
  const [detail, setDetail] = useState<ItemDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [activeImage, setActiveImage] = useState(0)

  // Fetch full detail (gallery + description) on open for eBay items.
  useEffect(() => {
    let cancelled = false
    setDetail(null)
    setActiveImage(0)
    if (listing.itemId) {
      setLoadingDetail(true)
      getItemDetail(listing.itemId)
        .then((d) => { if (!cancelled) setDetail(d) })
        .finally(() => { if (!cancelled) setLoadingDetail(false) })
    }
    return () => { cancelled = true }
  }, [listing.itemId])

  // Close on Escape and lock background scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  // Images: prefer the detail gallery, fall back to the listing thumbnail.
  const images = detail?.images?.length
    ? detail.images
    : listing.imageUrl
      ? [listing.imageUrl]
      : []

  const description =
    (detail && (detail.shortDescription || htmlToText(detail.description))) ||
    listing.description ||
    ''

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <h2 className="text-slate-900 dark:text-white font-semibold leading-snug pr-2">{listing.title}</h2>
            <div className="flex items-center gap-3 mt-2">
              <SourceBadge source={listing.source} />
              <span className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                {formatPrice(listing.price, listing.currency)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors p-1"
            aria-label="Close preview"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body (scrollable) */}
        <div className="overflow-y-auto p-5 space-y-5">
          {/* Image gallery */}
          {images.length > 0 ? (
            <div>
              <div className="rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-900 flex items-center justify-center" style={{ minHeight: '16rem' }}>
                <img src={images[activeImage]} alt={listing.title} className="max-h-[26rem] w-auto object-contain" />
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImage(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === activeImage ? 'border-blue-500' : 'border-transparent hover:border-slate-400 dark:hover:border-slate-500'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-slate-100 dark:bg-slate-900 h-48 flex items-center justify-center text-slate-400 dark:text-slate-600">
              <span className="text-4xl opacity-40">⌚</span>
            </div>
          )}

          {/* Details */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {listing.condition && (
              <Detail label="Condition" value={listing.condition} />
            )}
            {listing.seller && (
              <Detail label="Seller" value={listing.seller} />
            )}
            {listing.dateListed && (
              <Detail label="Listed" value={new Date(listing.dateListed).toLocaleDateString()} />
            )}
            {listing.matchedWatch && (
              <Detail label="Matched" value={listing.matchedWatch} />
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-slate-600 dark:text-slate-300 text-sm font-medium mb-2">Description</h3>
            {loadingDetail ? (
              <div className="space-y-2 animate-pulse">
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full" />
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-5/6" />
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
              </div>
            ) : description ? (
              <p className="text-slate-500 dark:text-slate-400 text-sm whitespace-pre-line leading-relaxed">{description}</p>
            ) : (
              <p className="text-slate-500 text-sm italic">No description available.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <a
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            View on {listing.source.includes('eBay') ? 'eBay' : listing.source}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-200/40 dark:bg-slate-700/40 rounded-lg px-3 py-2">
      <p className="text-slate-500 text-xs">{label}</p>
      <p className="text-slate-800 dark:text-slate-200 text-sm truncate" title={value}>{value}</p>
    </div>
  )
}
